<form
  data-testid="client-form"
  [formGroup]="form"
>
  @if (!loading()) {
    @if (showSelector) {
      <section
        class="client-form-wrapper flex column"
        data-testid="client-selector"
      >
        <div class="client-name-header flex vcenter">
          <h1 class="client-name-label">
            {{ "CASE_FORM.FORMS.CLIENT.TITLE" | translate }}
          </h1>
        </div>

        <div class="field-group">
          <span class="field-label">
            {{ "CASE_FORM.FORMS.CLIENT.SELECTOR_LABEL" | translate }}
          </span>
          <payee-form-field
            width="max"
            height="tall"
          >
            <payee-autocomplete
              data-testid="client-selector-autocomplete"
              [formControl]="selectedClientControl"
              [options]="clientOptions"
              [displayWith]="clientDisplayWith"
              [placeholder]="'CASE_FORM.FORMS.CLIENT.SELECTOR_PLACEHOLDER' | translate"
            ></payee-autocomplete>
            @if (assigning()) {
              <mat-progress-spinner
                payee-suffix
                mode="indeterminate"
                diameter="20"
              ></mat-progress-spinner>
            }
          </payee-form-field>

          <button
            type="button"
            class="add-client-link"
            data-testid="client-add-button"
            [disabled]="assigning()"
            (click)="addClient()"
          >
            {{ "CASE_FORM.FORMS.CLIENT.ADD_CLIENT" | translate }}
          </button>

          @if (showNotAllowedToEditError) {
            <span
              color="alert-red"
              data-testid="client-not-allowed-to-edit"
            >
              {{ "CASE_FORM.FORMS.CLIENT.NOT_ALLOWED_TO_EDIT" | translate }}
            </span>
          }

          @if (!showEditor) {
            <section
              class="client-summary-card flex vcenter client-selector-summary"
              data-testid="client-summary"
            >
              <payee-icon
                class="checkmark"
                name="checkmark-green-circle"
              ></payee-icon>
              <div class="summary-info grow1 flex column">
                <span
                  class="summary-name"
                  data-testid="client-summary-name"
                >
                  {{ client.name }}
                </span>
                <span
                  class="summary-meta flex vcenter"
                  data-testid="client-summary-meta"
                >
                  @if (client.tax_number) {
                    <span data-testid="client-summary-tax-number">{{ client.tax_number }}</span>
                  }
                  @if (client.tax_number && addressSummary) {
                    <span class="summary-dot">·</span>
                  }
                  @if (addressSummary) {
                    <span>{{ addressSummary }}</span>
                  }
                </span>
              </div>
            </section>
          }
        </div>
      </section>
    }
    @if (showEditor) {
      @if (collapsed()) {
        <section
          class="client-summary-card flex vcenter"
          role="button"
          tabindex="0"
          data-testid="client-summary"
          (click)="expandForm()"
          (keydown.enter)="expandForm()"
          (keydown.space)="expandForm(); $event.preventDefault()"
        >
          <payee-icon
            class="checkmark"
            name="checkmark-green-circle"
          ></payee-icon>
          <div class="summary-info grow1 flex column">
            <span class="summary-label">
              {{ "CASE_FORM.FORMS.CLIENT.TITLE" | translate }}
            </span>
            <span
              class="summary-name"
              data-testid="client-summary-name"
            >
              {{ client.name }}
            </span>
            <span
              class="summary-meta flex vcenter"
              data-testid="client-summary-meta"
            >
              @if (client.tax_number) {
                <span data-testid="client-summary-tax-number">{{ client.tax_number }}</span>
              }
              @if (client.tax_number && addressSummary) {
                <span class="summary-dot">·</span>
              }
              @if (addressSummary) {
                <span>{{ addressSummary }}</span>
              }
            </span>
          </div>
          @if (canExpand) {
            <payee-icon
              name="pencil"
              width="18px"
              height="18px"
            ></payee-icon>
          }
        </section>
      } @else {
        <section
          class="client-form-wrapper flex column"
          data-testid="client-editor"
        >
          @if (!showSelector) {
            <div class="client-name-header flex vcenter">
              <h1 class="client-name-label">
                {{ "CASE_FORM.FORMS.CLIENT.TITLE" | translate }}
              </h1>
            </div>
          }

          <div
            class="client-type-selector flex"
            data-testid="client-type-selector"
          >
            <button
              class="type-option"
              data-testid="client-type-org"
              [class.active]="client.client_type === 'org'"
              (click)="client_type.patchValue('org')"
            >
              <mat-icon>business</mat-icon>
              <span class="type-label">{{ "CASE_FORM.FORMS.CLIENT.TYPE_SELECTOR.ORG" | translate }}</span>
            </button>
            <button
              class="type-option"
              data-testid="client-type-house"
              [class.active]="client.client_type === 'house'"
              (click)="client_type.patchValue('house')"
            >
              <mat-icon>home</mat-icon>
              <span class="type-label">{{ "CASE_FORM.FORMS.CLIENT.TYPE_SELECTOR.HOUSE" | translate }}</span>
            </button>
          </div>

          @if (client.client_type === "org") {
            <div class="field-group">
              <span class="field-label">
                {{ "CASE_FORM.FORMS.CLIENT.TYPE_LABEL" | translate }}
              </span>
              <div
                #origin="matAutocompleteOrigin"
                class="client-name-input-container"
                matAutocompleteOrigin
                [class.autocomplete-open]="companyAuto.isOpen"
                [class.has-error]="name.invalid && name.dirty"
              >
                <img
                  alt=""
                  src="assets/shared/global-images/icon-set/find-gray.svg"
                  class="search-icon"
                  aria-hidden="true"
                />
                <input
                  class="client-name-input"
                  data-testid="client-name-input"
                  [formControl]="name"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.ORG.CLIENT_PLACEHOLDER_NAME' | translate"
                  [matAutocomplete]="companyAuto"
                  [matAutocompleteConnectedTo]="origin"
                  (input)="onCompanySearchInput($event)"
                  (blur)="onNameBlur()"
                />
                @if (autocompleteSearchLoading) {
                  <mat-progress-spinner
                    mode="indeterminate"
                    diameter="20"
                  ></mat-progress-spinner>
                }
              </div>
              <mat-autocomplete
                #companyAuto="matAutocomplete"
                class="client-autocomplete-panel"
                [requireSelection]="false"
                [displayWith]="companyDisplayWith"
                (optionSelected)="companySelected($event.option.value)"
              >
                <div class="options-scroll-area">
                  @for (option of companySearchOptions; track option.tax_number || option.name) {
                    <mat-option [value]="option">
                      <div class="company-option">
                        <span class="company-option-name">{{ option.short_name || option.name }}</span>
                        <span class="company-option-meta">
                          @if (option.tax_number) {
                            <span class="company-option-tax">
                              {{ formatTaxNumber(option.tax_number) }}
                            </span>
                          }
                          @if (formatCompanyAddress(option); as addr) {
                            <span class="company-option-address">
                              {{ addr }}
                            </span>
                          }
                        </span>
                      </div>
                    </mat-option>
                  }
                </div>
              </mat-autocomplete>
              @if (name.invalid && name.dirty) {
                <div class="client-name-error">
                  @if (name.hasError("required")) {
                    <mat-error>
                      {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                    </mat-error>
                  }
                </div>
              }

              <payee-form-field
                width="max"
                height="tall"
              >
                <input
                  payee-input
                  data-testid="client-tax-number"
                  [formControl]="tax_number"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.ORG.TAX_NUMBER_PLACEHOLDER' | translate"
                />
                @if (tax_number.invalid && tax_number.dirty) {
                  <ng-container payee-error>
                    @if (tax_number.hasError("required")) {
                      <span>
                        {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                      </span>
                    }
                    @if (tax_number.hasError("maxlength")) {
                      <span>
                        {{ "ERROR_MESSAGES.MAXLENGTH" | translate }}
                      </span>
                    }
                  </ng-container>
                }
              </payee-form-field>

              @if (addressVisible) {
                <div class="jurisdiction-field flex column">
                  <span class="field-label">
                    {{ "CASE_FORM.FORMS.CLIENT.ORG.HEADQUARTERS_LABEL" | translate }}
                  </span>
                  <payee-dropdown
                    width="max"
                    height="tall"
                    data-testid="client-country"
                    [formControl]="country_iso"
                    [options]="countryOptions"
                    [displayWith]="countryDisplayWith"
                    [filterable]="true"
                  >
                    <ng-template
                      #menuDisplayTemplate
                      let-selected
                    >
                      <div class="flex vcenter country-with-flag">
                        <span class="country-flag">{{ selected | countryFlag }}</span>
                        <span>{{ countryDisplayWith(selected) }}</span>
                      </div>
                    </ng-template>
                    <ng-template
                      #itemDisplayTemplate
                      let-option
                    >
                      <div class="flex vcenter country-with-flag">
                        <span class="country-flag">{{ option | countryFlag }}</span>
                        <span>{{ countryDisplayWith(option) }}</span>
                      </div>
                    </ng-template>
                  </payee-dropdown>
                </div>
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <input
                    payee-input
                    data-testid="client-postcode"
                    [formControl]="postcode"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.POSTCODE_PLACEHOLDER' | translate"
                  />
                  @if (postcode.invalid && postcode.dirty) {
                    <ng-container payee-error>
                      @if (postcode.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <input
                    payee-input
                    data-testid="client-settlement"
                    [formControl]="settlement"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.SETTLEMENT_PLACEHOLDER' | translate"
                  />
                  @if (settlement.invalid && settlement.dirty) {
                    <ng-container payee-error>
                      @if (settlement.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <input
                    payee-input
                    data-testid="client-street"
                    [formControl]="street"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.STREET_PLACEHOLDER' | translate"
                  />
                  @if (street.invalid && street.dirty) {
                    <ng-container payee-error>
                      @if (street.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
              }

              @if (mother_name.enabled) {
                @if (mother_name.value) {
                  <span class="field-label">
                    {{ "CASE_FORM.FORMS.CLIENT.HOUSE.MOTHER_NAME_PLACEHOLDER" | translate }}
                  </span>
                }
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <input
                    payee-input
                    [formControl]="mother_name"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.MOTHER_NAME_PLACEHOLDER' | translate"
                  />
                  @if (mother_name.invalid && mother_name.dirty) {
                    <ng-container payee-error>
                      @if (mother_name.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                      @if (mother_name.hasError("maxlength")) {
                        <span>
                          {{ "ERROR_MESSAGES.MAXLENGTH" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
              }

              @if (birth_place.enabled) {
                @if (birth_place.value) {
                  <span class="field-label">
                    {{ "CASE_FORM.FORMS.CLIENT.HOUSE.BIRTH_PLACE_PLACEHOLDER" | translate }}
                  </span>
                }
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <input
                    payee-input
                    [formControl]="birth_place"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.BIRTH_PLACE_PLACEHOLDER' | translate"
                  />
                  @if (birth_place.invalid && birth_place.dirty) {
                    <ng-container payee-error>
                      @if (birth_place.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                      @if (birth_place.hasError("maxlength")) {
                        <span>
                          {{ "ERROR_MESSAGES.MAXLENGTH" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
              }

              @if (birth_date.enabled) {
                @if (birth_date.value) {
                  <span class="field-label">
                    {{ "CASE_FORM.FORMS.CLIENT.HOUSE.BIRTH_DATE_PLACEHOLDER" | translate }}
                  </span>
                }
                <payee-form-field
                  width="max"
                  height="tall"
                >
                  <payee-datepicker
                    [formControl]="birth_date"
                    [max]="maxBirthDate"
                    [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.BIRTH_DATE_PLACEHOLDER' | translate"
                  ></payee-datepicker>
                  @if (birth_date.invalid && birth_date.dirty) {
                    <ng-container payee-error>
                      @if (birth_date.hasError("required")) {
                        <span>
                          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                        </span>
                      }
                      @if (birth_date.hasError("maxdate")) {
                        <span>
                          {{ "CASE_FORM.FORMS.CLIENT.HOUSE.ERRORS.MAX_DATE" | translate }}
                        </span>
                      }
                    </ng-container>
                  }
                </payee-form-field>
              }

              <button
                type="button"
                payee-button
                color="blue-payee"
                width="max"
                class="save-client-btn"
                data-testid="client-save"
                [disabled]="!form.valid || companyUpdateLoading()"
                (click)="saveAndCollapse()"
              >
                {{ "CASE_FORM.FORMS.INVOICE.SAVE" | translate }}
              </button>
              @if (showSaveRequiredError) {
                <div class="save-required-error">
                  <span color="alert-red">
                    {{ "CASE_FORM.FORMS.SAVE_REQUIRED" | translate }}
                  </span>
                </div>
              }
            </div>
          }

          @if (client.client_type === "house") {
            <div class="field-group">
              <span class="field-label">
                {{ "CASE_FORM.FORMS.CLIENT.TYPE_LABEL" | translate }}
              </span>
              <payee-form-field
                width="max"
                height="tall"
              >
                <input
                  payee-input
                  data-testid="client-house-name"
                  [formControl]="name"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.NAME_PLACEHOLDER' | translate"
                />
                @if (name.invalid && name.dirty) {
                  <ng-container payee-error>
                    @if (name.hasError("required")) {
                      <span>
                        {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                      </span>
                    }
                    @if (name.hasError("maxlength")) {
                      <span>
                        {{ "ERROR_MESSAGES.MAXLENGTH" | translate }}
                      </span>
                    }
                  </ng-container>
                }
              </payee-form-field>

              <div class="jurisdiction-field flex column">
                <span class="field-label">
                  {{ "CASE_FORM.FORMS.CLIENT.HOUSE.HEADQUARTERS_LABEL" | translate }}
                </span>
                <payee-dropdown
                  width="max"
                  height="tall"
                  data-testid="client-country"
                  [formControl]="country_iso"
                  [options]="countryOptions"
                  [displayWith]="countryDisplayWith"
                  [filterable]="true"
                >
                  <ng-template
                    #menuDisplayTemplate
                    let-selected
                  >
                    <div class="flex vcenter country-with-flag">
                      <span class="country-flag">{{ selected | countryFlag }}</span>
                      <span>{{ countryDisplayWith(selected) }}</span>
                    </div>
                  </ng-template>
                  <ng-template
                    #itemDisplayTemplate
                    let-option
                  >
                    <div class="flex vcenter country-with-flag">
                      <span class="country-flag">{{ option | countryFlag }}</span>
                      <span>{{ countryDisplayWith(option) }}</span>
                    </div>
                  </ng-template>
                </payee-dropdown>
              </div>
              <payee-form-field
                width="max"
                height="tall"
              >
                <input
                  payee-input
                  data-testid="client-postcode"
                  [formControl]="postcode"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.POSTCODE_PLACEHOLDER' | translate"
                />
                @if (postcode.invalid && postcode.dirty) {
                  <ng-container payee-error>
                    @if (postcode.hasError("required")) {
                      <span>
                        {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                      </span>
                    }
                  </ng-container>
                }
              </payee-form-field>
              <payee-form-field
                width="max"
                height="tall"
              >
                <input
                  payee-input
                  data-testid="client-settlement"
                  [formControl]="settlement"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.SETTLEMENT_PLACEHOLDER' | translate"
                />
                @if (settlement.invalid && settlement.dirty) {
                  <ng-container payee-error>
                    @if (settlement.hasError("required")) {
                      <span>
                        {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                      </span>
                    }
                  </ng-container>
                }
              </payee-form-field>
              <payee-form-field
                width="max"
                height="tall"
              >
                <input
                  payee-input
                  data-testid="client-street"
                  [formControl]="street"
                  [placeholder]="'CASE_FORM.FORMS.CLIENT.HOUSE.STREET_PLACEHOLDER' | translate"
                />
                @if (street.invalid && street.dirty) {
                  <ng-container payee-error>
                    @if (street.hasError("required")) {
                      <span>
                        {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                      </span>
                    }
                  </ng-container>
                }
              </payee-form-field>

              <button
                type="button"
                payee-button
                color="blue-payee"
                width="max"
                class="save-client-btn"
                data-testid="client-save"
                [disabled]="!form.valid || companyUpdateLoading()"
                (click)="saveAndCollapse()"
              >
                {{ "CASE_FORM.FORMS.INVOICE.SAVE" | translate }}
              </button>
              @if (showSaveRequiredError) {
                <div class="save-required-error">
                  <span color="alert-red">
                    {{ "CASE_FORM.FORMS.SAVE_REQUIRED" | translate }}
                  </span>
                </div>
              }
            </div>
          }
        </section>
      }
    }
  }

  @if (form.hasError("duplicate")) {
    <span color="alert-red">
      {{ "ERROR_MESSAGES.DUPLICATE_TAX_NUMBER" | translate }}
    </span>
  }
</form>
